// React
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import i18next from 'i18next';

// Components
import {
  TextInput,
  Dropdown,
  SingleSelectWithSearch,
  ToggleButton,
  PrimaryButton,
  SecondaryButton
} from '../../../msreact-kit/packages/html-components';
import Footer from '../../BaseLayout/footer';
import CouponDatePicker from './CustomDatePicker';

// Actions
import {
  getProductsDetailsForDropdown,
  getPricingPlansForProductById
} from '../../../actions/MyProductsAction';
import {
  getCouponDetailsById,
  getDiscountMasterData,
  createCoupon,
  updateCouponStatus
} from '../../../actions/discount-service-action';
import { setSnackbar } from '../../../actions/generic-action';
import Loader from '../../Shared/Utils/Loader';
import CustomizedSnackbars from '../../Shared/Utils/SnackBar';

// Data
import { COUPON_MODE, emptyForm } from './data';
import { TIMEZONE } from '../../../data/valueConstants';

// CSS
import './index.scss';

const requiredValidation = { rule: { required: { isRequired: true } } };

const DESC_MAX_LENGTH = 150;
const VALUE_MAX_LENGTH = 10;

const DISCOUNT_LABEL_MAP = {
  PERCENTAGE_OFF: 'Percentage',
  FIXED_AMOUNT_OFF: 'Fixed Amount'
};

const REGION_CURRENCY_MAP = {
  NA: 'USD',
  EU: 'EUR',
  IN: 'INR'
};

const CURRENCY_LABEL_MAP = {
  USD: 'USD',
  EUR: 'EUR',
  INR: 'INR'
};

const CouponDetails = (props) => {
  const queryParams = new URLSearchParams(props.location?.search);
  const mode = queryParams.get('mode');
  const coupon = props.location?.state?.coupon;

  const dispatch = useDispatch();

  const { content, snackbarStatus } = useSelector((state) => ({
    content: state.ContentReducer.content,
    snackbarStatus: state.GenericReducer.snackbarStatus
  }));
  const couponDetailsContent =
    content?.components?.discount_management?.coupon_details;
  const [form, setForm] = useState(coupon ? { ...coupon } : { ...emptyForm });
  const [errors, setErrors] = useState({});
  const [isActive, setIsActive] = useState(
    mode === COUPON_MODE.add ? true : coupon?.couponStatus === 'ACTIVE'
  );
  const [copied, setCopied] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [pricingPlanOptions, setPricingPlanOptions] = useState([]);
  const [pricingPlansLoading, setPricingPlansLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [discountTypeOptions, setDiscountTypeOptions] = useState([]);
  const [currencyTypeOptions, setCurrencyTypeOptions] = useState([]);
  const [expiresInOptions, setExpiresInOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = mode === COUPON_MODE.edit;
  const [charCountEntered, setCharCountEntered] = useState(
    isEdit ? form?.name?.length || 0 : 0
  );

  const isView = mode === COUPON_MODE.view;
  const region = process.env.REACT_APP_REGION;

  /* ── Fetch full coupon details in view mode ──
     Also fetches master data so codes can be converted to display labels. ── */
  useEffect(() => {
    if (!isView) return;
    const couponId = coupon?.id;
    if (!couponId) return;
    setDetailsLoading(true);
    Promise.all([
      dispatch(getCouponDetailsById(couponId)),
      dispatch(getDiscountMasterData())
    ])
      .then(([data, masterData]) => {
        if (!data) return;
        let normalized = { ...data };
        if (masterData) {
          const discountTypes = (masterData.DISCOUNT_TYPE || []).map((item) => ({
            code: item.code,
            label: DISCOUNT_LABEL_MAP[item.code] || item.code
          }));
          const expiryTypes = (masterData.EXPIRY_TYPE || []).map((item) => ({
            code: item.code,
            label: item.displayName || item.code
          }));
          // API returns `type` as code (e.g. "PERCENTAGE_OFF") → convert to label
          const matchedType = discountTypes.find(
            (o) => o.code === data.type || o.label === data.type
          );
          // API returns `expiryType` (e.g. "CUSTOM_DATE") → map to form field `expiresIn` as label
          const expiryCode = data.expiryType || data.expiresIn;
          const matchedExpiry = expiryTypes.find(
            (o) => o.code === expiryCode || o.label === expiryCode
          );
          normalized = {
            ...normalized,
            type: matchedType?.label || data.type,
            expiresIn: matchedExpiry?.label || expiryCode || ''
          };
        }
        setForm(normalized);
      })
      .finally(() => setDetailsLoading(false));
  }, []);

  /* ── Fetch products + master data on mount (add / edit only) ── */
  useEffect(() => {
    if (isView) return;

    setProductsLoading(true);
    dispatch(getProductsDetailsForDropdown())
      .then((options) => setProductOptions(options))
      .finally(() => setProductsLoading(false));

    // In edit mode, pre-fetch pricing plans for the existing product
    if (isEdit && coupon?.products?.[0]?.productId) {
      setPricingPlansLoading(true);
      dispatch(getPricingPlansForProductById(coupon.products[0].productId))
        .then((options) => setPricingPlanOptions(options))
        .finally(() => setPricingPlansLoading(false));
    }

    dispatch(getDiscountMasterData()).then((masterData) => {
      if (!masterData) return;

      // DISCOUNT_TYPE → discountTypeOptions
      const discountTypes = (masterData.DISCOUNT_TYPE || []).map((item) => ({
        id: item.id,
        label: DISCOUNT_LABEL_MAP[item.code] || item.code,
        value: DISCOUNT_LABEL_MAP[item.code] || item.code,
        code: item.code
      }));
      setDiscountTypeOptions(discountTypes);

      const currencyTypes = (masterData.CURRENCY_TYPE || [])
        .filter((item) => {
          const allowedCurrency = REGION_CURRENCY_MAP[region];
          return allowedCurrency ? item.code === allowedCurrency : true; // If no region match, show all
        })
        .map((item) => ({
          id: item.id,
          label: CURRENCY_LABEL_MAP[item.code] || item.code,
          value: CURRENCY_LABEL_MAP[item.code] || item.code,
          code: item.code
        }));
      setCurrencyTypeOptions(currencyTypes);

      // EXPIRY_TYPE → expiresInOptions
      const expiryTypes = (masterData.EXPIRY_TYPE || []).map((item) => ({
        id: item.id,
        label: item.displayName || item.code,
        value: item.displayName || item.code,
        code: item.code
      }));
      setExpiresInOptions(expiryTypes);

      // In edit mode, normalise form.type and form.expiresIn to display labels.
      // API returns `type` as a code and `expiryType` (not `expiresIn`), so check both fields.
      if (isEdit) {
        setForm((prev) => {
          const matchedType = discountTypes.find(
            (o) => o.code === prev.type || o.label === prev.type
          );
          // API field is `expiryType`; form field is `expiresIn` — check both
          const expiryCode = prev.expiresIn || prev.expiryType;
          const matchedExpiry = expiryTypes.find(
            (o) => o.code === expiryCode || o.label === expiryCode
          );
          // Normalize ISO date strings from API to DD/MM/YYYY for the date picker
          const normalizeDate = (date) =>
            date && !date.includes('/')
              ? moment(date).utcOffset(TIMEZONE.IN).format('DD/MM/YYYY')
              : date;
          return {
            ...prev,
            type: matchedType?.label || prev.type,
            expiresIn: matchedExpiry?.label || expiryCode || '',
            startDate: normalizeDate(prev.startDate),
            endDate: normalizeDate(prev.endDate)
          };
        });
      }
    });
  }, []);

  useEffect(() => {
    if (mode === COUPON_MODE.edit && form?.couponStatus) {
      setIsActive(form?.couponStatus === 'ACTIVE');
    }
  }, [form?.couponStatus, mode]);

  /* ── Handlers ── */
  const handleChange = (data) => {
    let val = data.value;
    let fieldError = data.error || '';

    if (data.name === 'name') {
      if (val.length > DESC_MAX_LENGTH) {
        setCharCountEntered(DESC_MAX_LENGTH);
        val = val.slice(0, DESC_MAX_LENGTH);
        fieldError = couponDetailsContent?.validation?.description_max_length;
      } else {
        setCharCountEntered(val.length);
      }
    }

    if (data.name === 'value') {
      // Show error only if non-numeric/non-decimal characters are entered
      if (/[^\d.]/.test(val)) {
        fieldError = couponDetailsContent?.validation?.value_numeric;
      }
      // Strip invalid characters silently
      val = val.replace(/[^\d.]/g, '');
      // Keep only first decimal point
      const parts = val.split('.');
      if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
      // Limit to 2 decimal places silently (no error)
      if (val.includes('.')) {
        const [integer, decimal] = val.split('.');
        val = integer + '.' + (decimal || '').slice(0, 2);
      }
      if (val.replace('.', '').length > VALUE_MAX_LENGTH) {
        val = val.slice(0, VALUE_MAX_LENGTH + 1);
        fieldError = couponDetailsContent?.validation?.value_max_length;
      }
    }

    setForm((prev) => ({ ...prev, [data.name]: val }));
    setErrors((prev) => ({ ...prev, [data.name]: fieldError }));
  };

  const handleBlur = (data) => {
    if (data.error) {
      setErrors((prev) => ({ ...prev, [data.name]: data.error }));
    }
  };

  const handleDropdownChange = (fieldName) => (data) => {
    const val = data.value?.label || data.value || '';
    setForm((prev) => ({ ...prev, [fieldName]: val }));
  };

  const handleDiscountTypeChange = (data) => {
    const val = data.value?.label || data.value || '';
    // Reset dependent fields whenever discount type changes
    setForm((prev) => ({ ...prev, type: val, value: '', currency: '' }));
  };

  const handleDateChange = (fieldName) => (data) => {
    setForm((prev) => ({ ...prev, [fieldName]: data.value }));
  };

  const handleProductChange = (data) => {
    // SingleSelectWithSearch always returns full option object as data.value
    const selected = data.value;
    const productId = selected?.id || '';
    const productName = selected?.label || '';
    setForm((prev) => ({
      ...prev,
      products: [{ productId, productName, plans: [{ planId: '', planName: '' }] }]
    }));
    setPricingPlanOptions([]);
    if (productId) {
      setPricingPlansLoading(true);
      dispatch(getPricingPlansForProductById(productId))
        .then((options) => setPricingPlanOptions(options))
        .finally(() => setPricingPlansLoading(false));
    }
  };

  const handlePlanChange = (data) => {
    const val = data.value?.label || data.value || '';
    const selectedPlan = pricingPlanOptions.find((o) => o.label === val);
    setForm((prev) => ({
      ...prev,
      products: [
        {
          ...prev.products?.[0],
          plans: [{ planId: selectedPlan?.id || '', planName: val }]
        }
      ]
    }));
  };

  const handleExpiresInChange = (data) => {
    const val = data.value?.label || data.value || '';
    // Reset both dates whenever expiry type changes; also clear endDate for Never Expires
    setForm((prev) => ({
      ...prev,
      expiresIn: val,
      startDate: '',
      endDate: ''
    }));
  };
  const handleBack = () => {
    props.history.push('/dashboard/discount-management');
  };

  const handleSubmit = async () => {
    if (mode === COUPON_MODE.add) {
      // Map form display values back to API codes
      const discountTypeCode =
        discountTypeOptions.find((o) => o.label === form.type)?.code || form.type;
      const expiryTypeCode =
        expiresInOptions.find((o) => o.label === form.expiresIn)?.code ||
        form.expiresIn;

      const payload = {
        name: form.name,
        description: form.name,
        discountType: discountTypeCode,
        discountValue: Number(form.value),
        currencyType: form.currencyType || null,
        codeType: 'GENERIC',
        expiryType: expiryTypeCode,
        startDate: (() => {
          if (!form.startDate) return '';
          const today = moment().utcOffset(TIMEZONE.IN).format('DD/MM/YYYY');
          const isToday = form.startDate === today;
          if (isToday) {
            return moment()
              .utcOffset(TIMEZONE.IN)
              .add(5, 'seconds')
              .format('YYYY-MM-DDTHH:mm:ssZ');
          }
          return moment(form.startDate, 'DD/MM/YYYY')
            .utcOffset(TIMEZONE.IN, true)
            .startOf('day')
            .add(1, 'second')
            .format('YYYY-MM-DDTHH:mm:ssZ');
        })(),
        endDate: form.endDate
          ? moment(form.endDate, 'DD/MM/YYYY')
              .utcOffset(TIMEZONE.IN, true)
              .endOf('day')
              .format('YYYY-MM-DDTHH:mm:ssZ')
          : '',
        totalUsageLimit: 1,
        products: form.products
      };

      setSubmitting(true);
      const result = await dispatch(createCoupon(payload));
      setSubmitting(false);
      if (result) {
        props.history.push({
          pathname: '/dashboard/discount-management',
          state: {
            action: mode,
            successMessage: couponDetailsContent?.messages?.coupon_created
          }
        });
      }
      return;
    }

    // Edit mode — call PUT API with new status
    setSubmitting(true);
    const couponId = coupon?.id;
    const result = await dispatch(updateCouponStatus(couponId, isActive));
    setSubmitting(false);
    if (result) {
      props.history.push({
        pathname: '/dashboard/discount-management',
        state: {
          action: mode,
          successMessage: couponDetailsContent?.messages?.coupon_updated
        }
      });
    }
  };

  const getTitle = () => {
    if (mode === COUPON_MODE.add) return couponDetailsContent?.title_create;
    if (mode === COUPON_MODE.edit) return couponDetailsContent?.title_edit;
    return couponDetailsContent?.title_view;
  };

  const getDropdownValue = (options, fieldValue) =>
    options.find((o) => o.label === fieldValue)?.label || '';

  const handleCopyCode = () => {
    if (form.code) {
      navigator.clipboard.writeText(form.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  /* ── Form validation ── */
  const isFormValid = (() => {
    const productId = form.products?.[0]?.productId;
    const planName = form.products?.[0]?.plans?.[0]?.planName;
    const isNeverExpires =
      expiresInOptions.find((o) => o.label === form.expiresIn)?.code ===
      'NEVER_EXPIRES';
    const mandatoryMet =
      productId &&
      planName &&
      form.type &&
      form.value &&
      form.expiresIn &&
      form.startDate &&
      (isNeverExpires || form.endDate) &&
      form.name;
    if (!mandatoryMet) return false;
    // Currency is required only for Fixed Amount
    if (form.type === 'Fixed Amount' && !form.currencyType) return false;
    // Block if field-level validation errors exist
    if (errors.name || errors.value) return false;
    return true;
  })();

  const statusClassMap = {
    ACTIVE: 'discount-grid__status--active',
    EXPIRED: 'discount-grid__status--expired',
    INACTIVE: 'discount-grid__status--deactivated',
    COMPLETED: 'discount-grid__status--used'
  };

  const statusLabels = {
    ACTIVE: couponDetailsContent?.status_labels?.activated,
    EXPIRED: couponDetailsContent?.status_labels?.expired,
    INACTIVE: couponDetailsContent?.status_labels?.deactivated,
    COMPLETED: couponDetailsContent?.status_labels?.used
  };

  const renderViewMode = () => {
    const isPercentage =
      form.type === 'Percentage' || form.type === 'PERCENTAGE_OFF';
    const discountValueDisplay = form.value
      ? `${form.value}${isPercentage ? '%' : ''}`
      : '—';
    return (
      <dl className='coupon-details__view-list'>
        {/* 1. Coupon Code */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.coupon_code}</dt>
          <dd className='coupon-details__coupon-code-row'>
            <span className='coupon-details__coupon-code-badge'>{form.code}</span>
            <button
              type='button'
              className={`coupon-details__copy-btn${copied ? ' coupon-details__copy-btn--copied' : ''}`}
              onClick={handleCopyCode}
              title={
                copied
                  ? couponDetailsContent?.copy_code?.copied
                  : couponDetailsContent?.copy_code?.label
              }
            >
              <i
                className={`a-icon ${copied ? 'boschicon-bosch-ic-checkmark' : 'boschicon-bosch-ic-copy'}`}
              />
            </button>
          </dd>
        </div>

        {/* 2. Product Name */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.product_name}</dt>
          <dd>{form.products?.[0]?.productName || '—'}</dd>
        </div>

        {/* 3. Pricing Plan */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.pricing_plan}</dt>
          <dd>{form.products?.[0]?.plans?.[0]?.planName || '—'}</dd>
        </div>

        {/* 4. Coupon Description */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.description}</dt>
          <dd>{form.name || '—'}</dd>
        </div>

        {/* 5. Discount Type */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.discount_type}</dt>
          <dd>{form.type || '—'}</dd>
        </div>

        {/* 6. Discount Value */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.discount_value}</dt>
          <dd>{discountValueDisplay}</dd>
        </div>

        {/* 7. Currency */}
        {form.type === DISCOUNT_LABEL_MAP.FIXED_AMOUNT_OFF && (
          <div className='coupon-details__view-row'>
            <dt>{couponDetailsContent?.fields?.currency}</dt>
            <dd>{form.currencyType || '—'}</dd>
          </div>
        )}

        {/* 8. Expires In */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.expires_in}</dt>
          <dd>{form.expiresIn || '—'}</dd>
        </div>

        {/* 9. Start Date */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.start_date}</dt>
          <dd>
            {form.startDate
              ? moment(form.startDate)
                  .utcOffset(TIMEZONE.IN)
                  .format('DD/MM/YYYY, hh:mm A')
              : '—'}
          </dd>
        </div>

        {/* 10. End Date */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.end_date}</dt>
          <dd>
            {form.endDate
              ? moment(form.endDate)
                  .utcOffset(TIMEZONE.IN)
                  .format('DD/MM/YYYY, hh:mm A')
              : '—'}
          </dd>
        </div>

        {/* 11. Status */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.status}</dt>
          <dd>
            <span
              className={`discount-grid__status ${statusClassMap[form.couponStatus] || ''}`}
            >
              {statusLabels[form.couponStatus] || '—'}
            </span>
            {form.orders?.[0]?.createdDate && (
              <span className='coupon-details__order-date'>
                {moment(form.orders[0].createdDate).format('DD/MM/YYYY, hh:mm A')}
              </span>
            )}
          </dd>
        </div>

        {/* 12. Order ID */}
        <div className='coupon-details__view-row'>
          <dt>{couponDetailsContent?.fields?.order_id}</dt>
          <dd>{form.orders?.[0]?.productOrderId || '—'}</dd>
        </div>
      </dl>
    );
  };

  /* ── Form Mode ── */
  const renderFormMode = () => (
    <div className='coupon-details__form'>
      <p className='coupon-details__form__section-title'>
        {couponDetailsContent?.section_title}
      </p>
      <p className='coupon-details__form__hint'>{couponDetailsContent?.hints}</p>

      {/* Show coupon code and status at the top in edit mode */}
      {isEdit && (
        <div className='coupon-details__form__row'>
          <div className='coupon-details__form__field'>
            <label className='coupon-details__form__static-label'>
              {couponDetailsContent?.fields?.coupon_code}
            </label>
            <div className='coupon-details__form__static-value'>
              <span className='coupon-details__coupon-code-badge'>{form.code}</span>
            </div>
          </div>
          <div className='coupon-details__form__field'>
            <label className='coupon-details__form__static-label'>
              {couponDetailsContent?.fields?.status}
            </label>
            <div className='coupon-details__form__static-value'>
              <span
                className={`discount-grid__status ${statusClassMap[form.couponStatus] || ''}`}
              >
                {statusLabels[form.couponStatus] || '—'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Row 1 — Product Name | Pricing Plan */}
      <div className='coupon-details__form__row'>
        <div className='coupon-details__form__field'>
          <SingleSelectWithSearch
            displayName={couponDetailsContent?.fields?.product_name}
            name='productName'
            options={productOptions}
            value={
              productOptions.find((o) => o.id === form.products?.[0]?.productId) ||
              null
            }
            handleChange={handleProductChange}
            validation={requiredValidation}
            error={errors.products}
            placeholder={couponDetailsContent?.placeholders?.product_name}
            disabled={isEdit}
          />
        </div>
        <div className='coupon-details__form__field'>
          <Dropdown
            displayName={couponDetailsContent?.fields?.pricing_plan}
            name='planName'
            options={pricingPlanOptions}
            value={getDropdownValue(
              pricingPlanOptions,
              form.products?.[0]?.plans?.[0]?.planName
            )}
            handleChange={handlePlanChange}
            validation={requiredValidation}
            disabled={
              isEdit || !form.products?.[0]?.productId || pricingPlansLoading
            }
          />
        </div>
      </div>

      {/* Row 2 — Discount Type (50%) | Value or Currency+Value (50%) based on type */}
      <div className='coupon-details__form__row'>
        <div className='coupon-details__form__field'>
          <Dropdown
            displayName={couponDetailsContent?.fields?.discount_type}
            name='type'
            options={discountTypeOptions}
            value={getDropdownValue(discountTypeOptions, form.type)}
            handleChange={handleDiscountTypeChange}
            validation={requiredValidation}
            disabled={isEdit}
          />
        </div>
        <div className='coupon-details__form__field-group'>
          {form.type === 'Fixed Amount' && (
            <div className='coupon-details__form__field'>
              <Dropdown
                displayName={couponDetailsContent?.fields?.currency}
                name='currency'
                options={currencyTypeOptions}
                value={getDropdownValue(currencyTypeOptions, form.currencyType)}
                handleChange={handleDropdownChange('currencyType')}
                validation={requiredValidation}
                disabled={isEdit}
              />
            </div>
          )}
          {form.type && (
            <div className='coupon-details__form__field'>
              <TextInput
                displayName={couponDetailsContent?.fields?.discount_value}
                name='value'
                value={form.value}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={errors.value}
                placeholder={couponDetailsContent?.placeholders?.discount_value_flat}
                validation={requiredValidation}
                disabled={isEdit}
              />
            </div>
          )}
        </div>
      </div>

      {/* Row 3 — Expires In (50%) | Start Date + End Date (25% + 25%) */}
      <div className='coupon-details__form__row'>
        <div className='coupon-details__form__field'>
          <Dropdown
            displayName={couponDetailsContent?.fields?.expires_in}
            name='expiresIn'
            options={expiresInOptions}
            value={getDropdownValue(expiresInOptions, form.expiresIn)}
            handleChange={handleExpiresInChange}
            validation={requiredValidation}
            disabled={isEdit}
          />
        </div>
        <div className='coupon-details__form__field-group'>
          <div className='coupon-details__form__field'>
            <CouponDatePicker
              label={couponDetailsContent?.fields?.start_date}
              value={form.startDate}
              onChange={handleDateChange('startDate')}
              required
              minDate={new Date()}
              disabled={isEdit}
            />
          </div>
          {expiresInOptions.find((o) => o.label === form.expiresIn)?.code !==
            'NEVER_EXPIRES' && (
            <div className='coupon-details__form__field'>
              <CouponDatePicker
                label={couponDetailsContent?.fields?.end_date}
                value={form.endDate}
                onChange={handleDateChange('endDate')}
                required
                disabled={isEdit || !form.startDate}
                minDate={
                  form.startDate
                    ? moment(form.startDate, 'DD/MM/YYYY').toDate()
                    : new Date()
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* Row 5 — Coupon Description | Status */}
      <div className='coupon-details__form__row'>
        <div className='coupon-details__form__field'>
          <TextInput
            displayName={couponDetailsContent?.fields?.description}
            name='name'
            value={form.name}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.name}
            placeholder={couponDetailsContent?.placeholders?.description}
            validation={requiredValidation}
            disabled={isEdit}
          />
          <div className='coupon-details__form__char'>
            {i18next.t('DYNAMIC_FORM.SHARED.ERROR_MESSAGE.MAX_CHAR')} -{' '}
            {DESC_MAX_LENGTH - charCountEntered}
          </div>
        </div>
        <div className='coupon-details__form__field'>
          <div
            className={`coupon-details__status-toggle${mode === COUPON_MODE.add ? ' coupon-details__status-toggle--disabled' : ''}`}
          >
            <span className='coupon-details__status-toggle__label'>
              {couponDetailsContent?.fields?.status}
            </span>
            <div className='coupon-details__status-toggle__row'>
              <ToggleButton
                id='coupon-status-toggle'
                name='couponStatus'
                label=''
                checked={isActive}
                handleToggle={(val) => {
                  if (mode === COUPON_MODE.edit) {
                    setIsActive(val);
                  }
                }}
                disabled={mode === COUPON_MODE.add}
              />
              <span className='coupon-details__status-toggle__state'>
                {isActive
                  ? couponDetailsContent?.status_labels?.activated
                  : couponDetailsContent?.status_labels?.deactivated}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleSnackBarClose = () => {
    dispatch(setSnackbar({ showSnackbar: false, message: '' }));
  };

  return (
    <>
      {(productsLoading || pricingPlansLoading || detailsLoading || submitting) && (
        <Loader />
      )}
      {snackbarStatus?.showSnackbar && (
        <CustomizedSnackbars
          severity={snackbarStatus?.severity}
          message={snackbarStatus?.message}
          changeState={handleSnackBarClose}
        />
      )}
      <div className='coupon-details__container'>
        <div className='coupon-details__container__title-wrapper'>
          <div className='coupon-details__container__title'>
            <h5 className='coupon-details__container__header -size-l'>
              {getTitle()}
            </h5>
            {!isView && (
              <p className='coupon-details__container__subtitle'>
                {couponDetailsContent?.subtitle}
              </p>
            )}
          </div>
        </div>

        <div className='coupon-details__container__content'>
          {isView ? renderViewMode() : renderFormMode()}

          <div className='coupon-details__container__buttons'>
            <SecondaryButton
              label={couponDetailsContent?.buttons?.back}
              onClick={handleBack}
              name='back'
            />
            {!isView && (
              <PrimaryButton
                label={
                  mode === COUPON_MODE.add
                    ? couponDetailsContent?.buttons?.create
                    : couponDetailsContent?.buttons?.update
                }
                onClick={handleSubmit}
                name='submit'
                disabled={!isFormValid}
              />
            )}
          </div>
        </div>
      </div>

      <Footer customClassName='d-lg-none' />
    </>
  );
};

CouponDetails.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

export default CouponDetails;
